import type { ProtectRegion, TenantsDto } from "./api/types.ts";
import { isRouteHandle } from "@sg-protect/federation-runtime";
import config, { DEFAULT_REGION } from "@sg-protect/runtime-config";
import { useEffect, useState } from "react";
import { useMatches } from "react-router";
import { fromProtectRegion } from "./protectRegion.ts";
import { reloadInRegion } from "./region-boundaries/reloadInRegion.ts";

// Setup lets the user pick the region until one is saved, and Migrate's legacy assessment pages always do. Both keep
// the pick only in the browser.
function useIsRegionPickedByUser(workspaceRegion: ProtectRegion | null) {
    return useMatches().some(
        ({ handle }) =>
            isRouteHandle(handle) &&
            (handle.isMigrateAssessmentFlow === true || (handle.isSetupFlow === true && workspaceRegion === null))
    );
}

/**
 * Keeps the page in the workspace's region from the startup tenants answer. Returns whether the page is reloading
 * into it, so the caller renders nothing that would call the wrong region's BFF.
 */
export function useWorkspaceRegion(tenants: TenantsDto | undefined) {
    const workspaceRegion = tenants?.region ?? null;
    const isRegionPickedByUser = useIsRegionPickedByUser(workspaceRegion);
    const shouldApply = tenants !== undefined && !isRegionPickedByUser;
    const region = workspaceRegion ? fromProtectRegion(workspaceRegion) : null;
    // The API clients take their base URL from the region once, when the page loads, and a request to the other
    // region's BFF finds none of the workspace's data.
    const [pageRegion] = useState(() => config.region);
    const isReloading = shouldApply && (region ?? DEFAULT_REGION) !== pageRegion;

    useEffect(() => {
        if (!shouldApply) {
            return;
        }

        if (region) {
            config.switch(region);
        } else {
            config.clear();
        }

        if (isReloading) {
            reloadInRegion(region);
        }
    }, [shouldApply, region, isReloading]);

    useEffect(() => {
        // Read after the effect above stores the workspace's region; where the user picks it, this is their pick.
        const storedRegion = config.storedRegion;

        function handlePageShow(event: PageTransitionEvent) {
            // Back/forward restores this page without a load, and the page shown in between may have stored its own
            // workspace's region.
            if (!event.persisted) {
                return;
            }

            if (storedRegion) {
                config.switch(storedRegion);
            } else {
                config.clear();
            }
        }

        window.addEventListener("pageshow", handlePageShow);

        return () => window.removeEventListener("pageshow", handlePageShow);
    }, [shouldApply, region]);

    return isReloading;
}
